import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const navLinks = [
  { label: "Intelligence", href: "/intelligence" },
  { label: "AI Pulse", href: "/pulse" },
  { label: "Research", href: "/research" },
  { label: "Adaptive Intelligence", href: "/adaptive-intelligence" },
  { label: "Speaking", href: "/speaking" },
  { label: "Media", href: "/media" },
  { label: "About", href: "/about" },
];

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-glass border-b border-[rgba(124,58,237,0.1)] shadow-lg shadow-black/20"
          : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link
            to="/"
            className="flex items-center gap-3 font-brand text-lg md:text-xl text-[var(--text-primary)] uppercase tracking-[0.2em]"
            data-testid="nav-logo"
          >
            <img src="/logo-rc-sm.png" alt="RC" className="h-9 md:h-11 w-auto" data-testid="nav-logo-mark" />
            <span className="hidden sm:inline">Raulvin Coke</span>
          </Link>

          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-sm font-medium transition-colors ${
                  location.pathname === link.href
                    ? "text-[var(--accent-violet-light)]"
                    : "text-[var(--text-secondary)] hover:text-white"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/contact"
              className="bg-[var(--accent-violet)] hover:bg-[#6d28d9] text-white px-5 py-2 rounded-lg text-sm font-semibold transition-all"
              data-testid="nav-contact-btn"
            >
              Contact
            </Link>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-[var(--text-primary)]"
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-glass border-t border-[rgba(124,58,237,0.1)]"
          >
            <div className="px-6 py-4 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="block py-3 text-[var(--text-secondary)] hover:text-white transition-colors"
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/contact"
                className="inline-block mt-3 bg-[var(--accent-violet)] text-white px-5 py-2.5 rounded-lg text-sm font-semibold"
              >
                Contact
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
